<template>
    <div class="pay-page">
  
      <!-- Loading -->
      <div v-if="loading" class="pay-loading">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3">Loading invoice...</p>
      </div>
  
      <template v-else>
  
        <!-- Top Bar -->
        <div class="pay-topbar">
          <div class="pay-topbar-left">
            <img v-if="company?.company_image" :src="host + company?.company_image" alt="Logo" class="topbar-logo" />
            <div>
              <p class="topbar-company">{{ company?.company_name }}</p>
              <p class="topbar-ref">Invoice #{{ invoice?.invoice_no }}</p>
            </div>
          </div>
          <div class="pay-topbar-right">
            <span class="inv-status-chip" :class="statusClass(invoice?.status)">{{ getStatus(invoice?.status) }}</span>
            <button class="btn-ghost" @click="downloadPDF">
              <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> Download PDF
            </button>
          </div>
        </div>
  
        <div class="pay-layout">
  
          <!-- ── Left: Payment Form ── -->
          <div class="pay-form-col">
  
            <!-- Already Paid -->
            <div v-if="invoice?.status == 2" class="paid-banner">
              <div class="paid-icon"><font-awesome-icon icon="check-circle" /></div>
              <div>
                <p class="paid-title">Invoice Fully Paid</p>
                <p class="paid-sub">This invoice has been settled. Thank you!</p>
              </div>
            </div>
  
            <template v-else>
              <div class="form-card">
                <div class="form-card-header">
                  <div class="form-card-icon"><font-awesome-icon icon="credit-card" /></div>
                  <div>
                    <p class="form-card-title">Make Payment</p>
                    <p class="form-card-sub">Complete your invoice payment securely</p>
                  </div>
                </div>
  
                <!-- Amount Due Chip -->
                <div class="amount-due-chip">
                  <span class="amount-due-label">Balance Due</span>
                  <span class="amount-due-value">{{ invoice?.currency?.symbol }}{{ formatMoney(invoice?.balance_amount) }}</span>
                </div>
  
                <div class="form-fields">
  
                  <div class="field-group">
                    <label class="field-label">Full Name <span class="req">*</span></label>
                    <input type="text" class="field-input" v-model="payment.full_name" placeholder="Your full name" />
                  </div>
  
                  <div class="field-group">
                    <label class="field-label">Email Address <span class="req">*</span></label>
                    <input type="email" class="field-input" v-model="payment.email" placeholder="you@email.com" />
                  </div>
  
                  <div class="field-group">
                    <label class="field-label">Phone Number <span class="req">*</span></label>
                    <input type="text" class="field-input" v-model="payment.phone_no" placeholder="+44 7700 900000" />
                  </div>
  
                  <div class="field-group">
                    <label class="field-label">Payment Gateway <span class="req">*</span></label>
                    <select class="field-input field-select" v-model="payment.payment_gateway_id" @change="getCurrentGateway">
                      <option value="" disabled>— Select gateway —</option>
                      <option v-for="g in paymentTypes" :key="g.id" :value="g.id">{{ g.name }}</option>
                    </select>
                    <!-- Gateway Logos -->
                    <div class="gateway-logos" v-if="paymentTypes.length">
                      <img v-for="g in paymentTypes" :key="g.id" :src="g?.logo" :alt="g.name" class="gateway-logo" />
                    </div>
                  </div>
  
                  <div class="field-group">
                    <label class="field-label">Payment Type <span class="req">*</span></label>
                    <select class="field-input field-select" v-model="payment.payment_type" @change="handlePaymentType">
                      <option value="full_payment">Full Payment</option>
                      <option value="part_payment">Part Payment</option>
                    </select>
                  </div>
  
                  <div class="field-group">
                    <label class="field-label">Payment Date <span class="req">*</span></label>
                    <div class="datepicker-wrap">
                      <font-awesome-icon icon="calendar" class="datepicker-icon" />
                      <Datepicker v-model="payment.payment_date" :enableTimePicker="false" class="clean-datepicker" />
                    </div>
                  </div>
  
                  <div class="field-group">
                    <label class="field-label">Payment Amount <span class="req">*</span></label>
                    <div class="amount-input-wrap">
                      <span class="amount-currency">{{ invoice?.currency?.symbol }}</span>
                      <input
                        type="text"
                        class="field-input amount-input"
                        v-model="payment.amount"
                        :readonly="payment.payment_type === 'full_payment'"
                        onkeypress="return /^[0-9.]+$/i.test(event.key)"
                        @keyup="handleAmount"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
  
                  <div class="field-group">
                    <label class="field-label">Remaining Balance</label>
                    <div class="balance-display" :class="payment.balance > 0 ? 'balance-display--outstanding' : 'balance-display--clear'">
                      <font-awesome-icon :icon="payment.balance > 0 ? 'exclamation-circle' : 'check-circle'" class="me-2" />
                      {{ invoice?.currency?.symbol }}{{ formatMoney(payment.balance) }}
                    </div>
                  </div>
  
                </div>
  
                <!-- Payment Buttons -->
                <div class="payment-btns" v-if="canProceed">
  
                  <!-- Paystack -->
                  <paystack v-if="current_gateway.slug == 'paystack'"
                    class="pay-btn pay-btn--paystack"
                    buttonClass="''"
                    buttonText="Pay with Paystack"
                    :publicKey="current_gateway?.public_key"
                    :email="payment.email"
                    :amount="payment.amount * 100"
                    :reference="'psk_' + reference"
                    :onSuccess="onSuccessfulPayment"
                    :onCanel="onCancelledPayment"
                  />
  
                  <!-- Flutterwave -->
                  <button v-if="current_gateway.slug == 'flutterwave'" class="pay-btn pay-btn--flutter" @click="makePayment">
                    <font-awesome-icon icon="bolt" class="me-2" />
                    Pay with Flutterwave
                  </button>
  
                </div>
  
                <p v-else class="pay-hint">
                  <font-awesome-icon icon="info-circle" class="me-1" />
                  Fill in all required fields to enable payment.
                </p>
  
              </div>
            </template>
  
            <!-- Secure badge -->
            <div class="secure-badge">
              <font-awesome-icon icon="lock" class="me-2" />
              Payments are processed securely
            </div>
  
          </div>
  
          <!-- ── Right: Invoice Preview ── -->
          <div class="pay-invoice-col" id="printMe">
  
            <!-- Invoice Header -->
            <div class="inv-header">
              <div class="inv-header-left">
                <p class="inv-label">INVOICE</p>
                <p class="inv-number">#{{ invoice?.invoice_no }}</p>
              </div>
              <div class="inv-header-right">
                <img v-if="company?.company_image" :src="host + company?.company_image" alt="Logo" class="inv-logo" />
              </div>
            </div>
  
            <!-- Parties -->
            <div class="inv-parties">
              <div>
                <p class="party-label">Bill To</p>
                <p class="party-name">{{ invoice?.customer?.customer_name }}</p>
                <p class="party-detail">{{ invoice?.customer?.address }}</p>
                <p class="party-detail">{{ invoice?.customer?.email }}</p>
              </div>
              <div>
                <p class="party-label">From</p>
                <p class="party-name">{{ company?.company_name }}</p>
                <p class="party-detail">{{ company?.address }}</p>
                <p class="party-detail">{{ company?.company_email }}</p>
                <p class="party-detail">{{ company?.phone }}</p>
              </div>
            </div>
  
            <!-- Meta -->
            <div class="inv-meta">
              <div class="inv-meta-item"><span class="inv-meta-key">Invoice Date</span><span class="inv-meta-val">{{ dateTime(invoice?.invoice_date) }}</span></div>
              <div class="inv-meta-item"><span class="inv-meta-key">Due Date</span><span class="inv-meta-val">{{ dateTime(invoice?.due_date) }}</span></div>
              <div class="inv-meta-item"><span class="inv-meta-key">Currency</span><span class="inv-meta-val">{{ invoice?.currency?.name }} ({{ invoice?.currency?.symbol }})</span></div>
            </div>
  
            <!-- Line Items -->
            <div class="inv-items-wrap">
              <table class="inv-table">
                <thead>
                  <tr>
                    <th style="width:40%">Description</th>
                    <th class="tc">Qty</th>
                    <th class="tr">Price</th>
                    <th class="tr">Tax</th>
                    <th class="tr">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in invoice?.items" :key="i">
                    <td>{{ item?.description }}</td>
                    <td class="tc text-muted-cell">{{ item?.quantity }}</td>
                    <td class="tr">{{ $filters.formatMoney(item?.price) }}</td>
                    <td class="tr text-muted-cell">
                      <span v-if="item?.apply_tax">{{ $filters.formatMoney(item?.tax_amount) }}</span>
                      <span v-else class="na-pill">N/A</span>
                    </td>
                    <td class="tr fw-bold">{{ $filters.formatMoney(item?.total) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
  
            <!-- Totals -->
            <div class="inv-totals">
              <div class="inv-total-row"><span>Subtotal</span><span>{{ $filters.formatMoney(invoice?.sub_total_amount, invoice?.currency?.symbol) }}</span></div>
              <div class="inv-total-row" v-if="invoice?.discount_amount > 0"><span>Discount</span><span class="deduction-value">− {{ $filters.formatMoney(invoice?.discount_amount, invoice?.currency?.symbol) }}</span></div>
              <div class="inv-total-row" v-if="invoice?.tax_amount > 0"><span>Tax</span><span>{{ $filters.formatMoney(invoice?.tax_amount, invoice?.currency?.symbol) }}</span></div>
              <div class="inv-total-divider"></div>
              <div class="inv-total-row inv-total-row--grand"><span>Total</span><span>{{ $filters.formatMoney(invoice?.total_amount, invoice?.currency?.symbol) }}</span></div>
            </div>
  
            <!-- Payment Summary -->
            <div class="inv-payment-summary">
              <div class="inv-total-row"><span>Amount Paid</span><span class="earning-value fw-bold">{{ $filters.formatMoney(invoice?.paid_amount, invoice?.currency?.symbol) }}</span></div>
              <div class="inv-total-row" :class="invoice?.balance_amount > 0 ? 'inv-balance--outstanding' : ''">
                <span>Balance Due</span>
                <span class="fw-bold" :class="invoice?.balance_amount > 0 ? 'deduction-value' : 'earning-value'">
                  {{ $filters.formatMoney(invoice?.balance_amount ?? 0, invoice?.currency?.symbol) }}
                </span>
              </div>
            </div>
  
            <!-- Bank Details -->
            <div class="inv-bank" v-if="invoice?.bank && invoice?.balance_amount > 0">
              <p class="inv-section-title"><font-awesome-icon icon="university" class="me-2" />Bank Details</p>
              <div class="inv-total-row"><span>Bank</span><span class="fw-bold">{{ invoice?.bank?.bank_name }}</span></div>
              <div class="inv-total-row"><span>Account Name</span><span class="fw-bold">{{ invoice?.bank?.account_name }}</span></div>
              <div class="inv-total-row"><span>Account No</span><span class="fw-bold">{{ invoice?.bank?.account_no }}</span></div>
            </div>
  
            <!-- Doc Footer -->
            <div class="inv-doc-footer"><span v-html="poweredBy()"></span></div>
  
          </div>
  
        </div>
      </template>
    </div>
  </template>
  
  <script>
  import { useToast }       from 'vue-toastification';
  import axios              from 'axios';
  import { useStore }       from 'vuex';
  import { useRouter }      from 'vue-router';
  import host               from '../../../main';
  import jsPDF              from 'jspdf';
  import html2canvas        from 'html2canvas';
  import { reusableMixin }  from '../../../lib/reusableMixin';
  import paystack           from 'vue3-paystack';
  import { useFlutterwave } from 'flutterwave-vue3';
  
  export default {
    components: { paystack },
    mixins: [reusableMixin],
  
    data() {
      return {
        invoice:         null,
        company:         null,
        paymentTypes:    [],
        current_gateway: null,
        loading:         true,
        crudLoading:     false,
        toast:           useToast(),
        router:          useRouter(),
        store:           useStore(),
  
        payment: {
          amount:             0,
          balance:            0,
          payment_gateway_id: '',
          payment_type:       'full_payment',
          payment_date:       new Date(),
          invoice_id:         '',
          full_name:          null,
          email:              null,
          phone_no:           null,
          reference:          null,
        },
      };
    },
  
    computed: {
      host() { return host.hostName; },
      reference() { return Date.now() + (Math.random() * 100000).toFixed(); },
      canProceed() {
        return this.payment.full_name && this.payment.email && this.payment.phone_no
            && this.payment.amount > 0 && this.payment.payment_gateway_id !== '';
      },
    },
  
    created() { this.fetchData(); },
  
    methods: {
      getStatus(status) {
        return ['Pending','Partial Payment','Completed','Overdue','Abandoned'][status] ?? '—';
      },
      statusClass(status) {
        return ['status--pending','status--partial','status--completed','status--overdue','status--abandoned'][status] ?? '';
      },
      formatMoney(val) {
        if (!val && val !== 0) return '0.00';
        return Number(val).toLocaleString(undefined, { minimumFractionDigits:2, maximumFractionDigits:2 });
      },
  
      async fetchData() {
        this.loading = true;
        axios.get(`/invoice/online-detail/${this.$route.params.company}/${this.$route.params.invoice}`)
          .then(response => {
            this.loading       = false;
            const d            = response.data.data;
            this.invoice       = d.invoice;
            this.company       = d.company;
            this.paymentTypes  = d.gateway;
            this.payment.balance     = this.invoice.balance_amount;
            this.payment.invoice_id  = this.invoice.id;
            this.payment.full_name   = this.invoice?.customer?.customer_name;
            this.payment.email       = this.invoice?.customer?.email;
            this.payment.phone_no    = this.invoice?.customer?.phone_no;
            this.payment.amount      = this.invoice?.balance_amount;
          })
          .catch(() => { this.loading = false; this.toast.error('Could not load invoice'); });
      },
  
      async getCurrentGateway() {
        if (!this.payment.payment_gateway_id) return;
        axios.get(`/gateway/details/${this.$route.params.company}/${this.payment.payment_gateway_id}`)
          .then(response => { this.current_gateway = response.data.data; })
          .catch(() => {});
      },
  
      handlePaymentType() {
        if (this.payment.payment_type === 'full_payment') {
          this.payment.amount  = this.invoice?.balance_amount;
          this.payment.balance = this.invoice?.balance_amount;
        } else {
          this.payment.balance = Math.max(0, (this.invoice?.balance_amount - this.payment.amount)).toFixed(2);
        }
      },
  
      handleAmount() {
        const bal = this.invoice?.balance_amount;
        let remaining = (bal - this.payment.amount).toFixed(2);
        if (remaining < 0) { this.payment.amount = bal; remaining = 0; }
        this.payment.balance = remaining;
      },
  
      validatePayment() {
        if (!this.payment.full_name)          { this.toast.error('Full name is required'); return false; }
        if (!this.payment.email)              { this.toast.error('Email is required'); return false; }
        if (!this.payment.phone_no)           { this.toast.error('Phone number is required'); return false; }
        if (!this.payment.payment_gateway_id) { this.toast.error('Select a payment gateway'); return false; }
        if (!this.payment.payment_date)       { this.toast.error('Select a payment date'); return false; }
        if (!this.payment.amount || this.payment.amount < 1) { this.toast.error('Payment amount must be greater than 0'); return false; }
        return true;
      },
  
      makePayment() {
        if (!this.validatePayment()) return;
        useFlutterwave({
          amount:         this.payment.amount,
          country:        'NG',
          currency:       this.invoice?.currency?.code,
          customer:       { email: this.payment.email, name: this.payment.full_name, phone_number: this.payment.phone_no },
          customizations: { description:'', logo:'', title:'Mapiibs' },
          meta:           { token: 'FLW-' + this.reference },
          payment_options: 'card',
          public_key:     this.current_gateway?.public_key,
          tx_ref:         'flw_' + this.reference,
          redirect_url:   undefined,
          onclose:        () => {},
          callback: (data) => {
            if (data.status === 'successful') {
              const payload = {
                amount: data.amount, payment_gateway_id: this.payment.payment_gateway_id,
                invoice_id: this.invoice.id, payee_name: data.customer.name,
                payee_email: data.customer.email, payee_phone: data.customer.phone_number,
                reference: data.tx_ref, response_meta: data,
              };
              axios.post(`/invoice/record-online-payment/${this.$route.params.company}`, payload)
                .then(res => { this.toast.success(res.data.message); setTimeout(() => location.reload(), 1000); })
                .catch(() => {});
            } else {
              this.toast.error('Payment failed. Please try again.');
              setTimeout(() => location.reload(), 1000);
            }
          },
        });
      },
  
      onSuccessfulPayment(data) {
        const payload = {
          amount: this.payment.amount, payment_gateway_id: this.payment.payment_gateway_id,
          payment_date: new Date(), invoice_id: this.invoice.id,
          payee_name: this.payment.full_name, payee_email: this.payment.email,
          payee_phone: this.payment.phone_no, reference: data.trxref, response_meta: data,
        };
        axios.post(`/invoice/record-online-payment/${this.$route.params.company}`, payload)
          .then(res => { this.toast.success(res.data.message); setTimeout(() => location.reload(), 1000); })
          .catch(error => { this.toast.error(error.response?.data?.message ?? 'Payment recording failed'); });
      },
      onCancelledPayment() {},
  
      async downloadPDF() {
        const el     = document.getElementById('printMe');
        const canvas = await html2canvas(el, { scale:2, useCORS:true });
        const pdf    = new jsPDF('p', 'mm', 'a4');
        const w      = pdf.internal.pageSize.getWidth();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, w, (canvas.height * w) / canvas.width);
        pdf.save(`Invoice_${this.invoice?.invoice_no}.pdf`);
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page ── */
  .pay-page { min-height:100vh; background:#f4f7fb; }
  
  /* ── Loading ── */
  .pay-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:60vh; color:#7a90b0; }
  .mt-3 { margin-top:12px; }
  
  /* ── Top Bar ── */
  .pay-topbar { display:flex; align-items:center; justify-content:space-between; padding:14px 32px; background:#fff; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .pay-topbar-left  { display:flex; align-items:center; gap:12px; }
  .topbar-logo      { width:40px; height:40px; object-fit:contain; border-radius:8px; }
  .topbar-company   { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .topbar-ref       { font-size:12px; color:#7a90b0; margin:0; }
  .pay-topbar-right { display:flex; align-items:center; gap:10px; }
  .btn-ghost { background:transparent; border:1px solid #dde5f0; border-radius:8px; padding:7px 14px; font-size:13px; color:#1a2b45; cursor:pointer; display:flex; align-items:center; transition:background .15s; }
  .btn-ghost:hover { background:#f4f7fb; }
  .me-1 { margin-right:4px; } .me-2 { margin-right:7px; }
  
  /* Status chips */
  .inv-status-chip { display:inline-block; border-radius:6px; padding:4px 12px; font-size:12px; font-weight:700; border:1px solid transparent; }
  .status--pending   { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }
  .status--partial   { background:#edf4ff; color:#004AAD; border-color:#c5d6f5; }
  .status--completed { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .status--overdue   { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
  .status--abandoned { background:#f4f7fb; color:#7a90b0; border-color:#e4eaf2; }
  
  /* ── Layout ── */
  .pay-layout { display:grid; grid-template-columns:360px 1fr; gap:24px; padding:24px 32px; align-items:start; }
  
  /* ── Payment Form ── */
  .pay-form-col { display:flex; flex-direction:column; gap:14px; }
  .form-card    { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
  .form-card-header { display:flex; align-items:center; gap:12px; margin-bottom:16px; }
  .form-card-icon   { width:40px; height:40px; border-radius:10px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .form-card-title  { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .form-card-sub    { font-size:12px; color:#7a90b0; margin:0; }
  
  .amount-due-chip { display:flex; justify-content:space-between; align-items:center; background:rgba(0,74,173,.06); border:1px solid #c5d6f5; border-radius:10px; padding:12px 16px; margin-bottom:18px; }
  .amount-due-label { font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#004AAD; }
  .amount-due-value { font-size:20px; font-weight:800; color:#004AAD; }
  
  .form-fields { display:flex; flex-direction:column; gap:13px; }
  .field-group  { display:flex; flex-direction:column; gap:5px; }
  .field-label  { font-size:13px; font-weight:600; color:#1a2b45; }
  .req          { color:#e03535; }
  .field-input  { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; }
  .field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .field-input[readonly] { background:#f8fafc; color:#4a5e78; }
  .field-select { appearance:auto; cursor:pointer; }
  
  .datepicker-wrap { position:relative; }
  .datepicker-icon { position:absolute; left:11px; top:50%; transform:translateY(-50%); color:#7a90b0; font-size:13px; z-index:2; pointer-events:none; }
  :deep(.clean-datepicker) { border:1px solid #dde5f0 !important; border-radius:8px !important; background:#fff !important; padding-left:32px !important; }
  :deep(.clean-datepicker input) { font-size:13.5px; color:#1a2b45; }
  
  .amount-input-wrap { display:flex; align-items:center; border:1px solid #dde5f0; border-radius:8px; overflow:hidden; }
  .amount-currency   { padding:9px 12px; background:#f4f7fb; color:#7a90b0; font-weight:700; font-size:13.5px; border-right:1px solid #dde5f0; flex-shrink:0; }
  .amount-input      { border:none !important; border-radius:0 !important; box-shadow:none !important; }
  
  .balance-display { display:flex; align-items:center; border-radius:8px; padding:10px 14px; font-size:14px; font-weight:700; }
  .balance-display--outstanding { background:#fff0f0; color:#e03535; }
  .balance-display--clear       { background:#f0fdf4; color:#16a34a; }
  
  .gateway-logos { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-top:8px; }
  .gateway-logo  { max-height:32px; object-fit:contain; }
  
  .payment-btns  { margin-top:20px; display:flex; flex-direction:column; gap:10px; }
  .pay-btn { display:flex; align-items:center; justify-content:center; width:100%; padding:12px 20px; border-radius:8px; border:none; font-size:14px; font-weight:600; cursor:pointer; transition:background .15s; }
  .pay-btn--paystack { background:#004AAD; color:#fff; }
  .pay-btn--paystack:hover { background:#003b8e; }
  .pay-btn--flutter  { background:#f5a623; color:#fff; }
  .pay-btn--flutter:hover  { background:#e09520; }
  
  .pay-hint { font-size:12.5px; color:#7a90b0; margin-top:16px; text-align:center; }
  
  /* Paid state */
  .paid-banner { display:flex; align-items:center; gap:16px; background:#f0fdf4; border:1px solid #bbf7d0; border-radius:12px; padding:20px; }
  .paid-icon   { width:48px; height:48px; border-radius:50%; background:rgba(22,163,74,.15); color:#16a34a; display:flex; align-items:center; justify-content:center; font-size:22px; flex-shrink:0; }
  .paid-title  { font-size:15px; font-weight:700; color:#16a34a; margin:0 0 4px; }
  .paid-sub    { font-size:13px; color:#4a5e78; margin:0; }
  
  .secure-badge { display:flex; align-items:center; justify-content:center; font-size:12.5px; color:#7a90b0; padding:8px; }
  
  /* ── Invoice Preview ── */
  .pay-invoice-col { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  .inv-header { display:flex; justify-content:space-between; align-items:center; padding:28px 28px 20px; background:linear-gradient(135deg,#051B35,#004AAD); }
  .inv-label  { font-size:11px; text-transform:uppercase; letter-spacing:.1em; color:rgba(255,255,255,.6); margin:0 0 4px; }
  .inv-number { font-size:26px; font-weight:900; color:#fff; margin:0; }
  .inv-logo   { max-width:80px; max-height:50px; object-fit:contain; border-radius:6px; background:#fff; padding:4px; }
  
  .inv-parties { display:grid; grid-template-columns:1fr 1fr; gap:24px; padding:20px 28px; border-bottom:1px solid #e4eaf2; }
  .party-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; margin:0 0 6px; }
  .party-name  { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 3px; text-transform:uppercase; }
  .party-detail { font-size:12.5px; color:#4a5e78; margin:0 0 2px; }
  
  .inv-meta { display:flex; gap:0; border-bottom:1px solid #e4eaf2; }
  .inv-meta-item { flex:1; padding:12px 16px; border-right:1px solid #e4eaf2; }
  .inv-meta-item:last-child { border-right:none; }
  .inv-meta-key { display:block; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; margin-bottom:4px; }
  .inv-meta-val { font-size:13px; font-weight:600; color:#1a2b45; }
  
  .inv-items-wrap { overflow-x:auto; }
  .inv-table { width:100%; border-collapse:collapse; min-width:480px; }
  .inv-table thead tr { background:#f8fafc; }
  .inv-table thead th { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:10px 16px; border-bottom:1px solid #e4eaf2; border-top:1px solid #e4eaf2; }
  .inv-table tbody tr { border-bottom:1px solid #f0f4f8; }
  .inv-table tbody tr:last-child { border-bottom:none; }
  .inv-table tbody td { padding:11px 16px; font-size:13px; }
  .tc { text-align:center; }
  .tr { text-align:right; }
  .fw-bold { font-weight:700; }
  .text-muted-cell { color:#4a5e78; }
  .na-pill { font-size:11px; color:#aab8cc; background:#f4f7fb; border-radius:4px; padding:1px 6px; }
  
  .inv-totals,.inv-payment-summary,.inv-bank { padding:16px 28px; border-top:1px solid #e4eaf2; }
  .inv-total-row  { display:flex; justify-content:space-between; align-items:center; font-size:13.5px; color:#4a5e78; margin-bottom:8px; }
  .inv-total-row:last-child { margin-bottom:0; }
  .inv-total-divider { height:1px; background:#e4eaf2; margin:8px 0; }
  .inv-total-row--grand { font-size:17px; font-weight:900; color:#1a2b45; }
  .inv-total-row--grand span:last-child { color:#004AAD; }
  .inv-balance--outstanding { background:rgba(224,53,53,.04); border-radius:8px; padding:6px 8px; margin-left:-8px; margin-right:-8px; }
  .deduction-value { color:#e03535; }
  .earning-value   { color:#16a34a; }
  .inv-section-title { font-size:13px; font-weight:700; color:#1a2b45; margin:0 0 12px; display:flex; align-items:center; }
  .inv-doc-footer { text-align:center; padding:14px; background:#f8fafc; border-top:1px solid #e4eaf2; font-size:12px; color:#aab8cc; }
  
  /* ── Responsive ── */
  @media(max-width:991px) { .pay-layout { grid-template-columns:1fr; } .pay-topbar,.pay-topbar-left { flex-direction:column; align-items:flex-start; } }
  @media(max-width:767px) { .pay-layout { padding:16px; } .inv-parties { grid-template-columns:1fr; gap:12px; } .inv-meta { flex-direction:column; } .inv-meta-item { border-right:none; border-bottom:1px solid #e4eaf2; } }
  </style>